import React, { useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Info } from "lucide-react";
import { resolveImage } from "../lib/api";

const SWIPE_THRESHOLD = 120;

export default function SwipeCard({ product, index, onSwipe, onInfo, isTop }) {
    const x = useMotionValue(0);
    const rotate = useTransform(x, [-300, 0, 300], [-18, 0, 18]);
    const buyOpacity = useTransform(x, [40, 160], [0, 1]);
    const dontOpacity = useTransform(x, [-160, -40], [1, 0]);
    const [dragging, setDragging] = useState(false);

    const handleDragEnd = (_, info) => {
        setDragging(false);
        if (info.offset.x > SWIPE_THRESHOLD) {
            onSwipe?.(product, "buy");
        } else if (info.offset.x < -SWIPE_THRESHOLD) {
            onSwipe?.(product, "dont_buy");
        }
    };

    const cardClick = () => {
        if (!dragging) onInfo?.(product);
    };

    return (
        <motion.div
            data-testid={`swipe-card-${product.id}`}
            className="absolute w-full h-full rounded-2xl overflow-hidden bg-[#121212] border border-white/10 kw-card-shadow cursor-grab active:cursor-grabbing no-select"
            style={{
                x: isTop ? x : 0,
                rotate: isTop ? rotate : 0,
                zIndex: 100 - index,
                scale: 1 - index * 0.04,
                top: index * 8,
            }}
            drag={isTop ? "x" : false}
            dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
            dragElastic={0.9}
            onDragStart={() => setDragging(true)}
            onDragEnd={handleDragEnd}
            whileTap={isTop ? { cursor: "grabbing" } : {}}
        >
            <img
                src={resolveImage(product.image_url)}
                alt={product.title}
                className="w-full h-full object-cover pointer-events-none"
                draggable={false}
            />

            {/* Labels */}
            {isTop && (
                <>
                    <motion.div
                        style={{ opacity: buyOpacity }}
                        className="absolute top-8 left-6 px-4 py-2 rounded-md border-4 border-[#FF5A00] kw-orange font-display text-3xl uppercase -rotate-12"
                    >
                        OH YES
                    </motion.div>
                    <motion.div
                        style={{ opacity: dontOpacity }}
                        className="absolute top-8 right-6 px-4 py-2 rounded-md border-4 border-white text-white font-display text-3xl uppercase rotate-12"
                    >
                        OH NO
                    </motion.div>
                </>
            )}

            {/* Info button */}
            <button
                data-testid={`card-info-btn-${product.id}`}
                onClick={(e) => {
                    e.stopPropagation();
                    onInfo?.(product);
                }}
                aria-label="Show details"
                className="absolute top-4 right-4 bg-black/60 backdrop-blur-md rounded-full p-2.5 text-white hover:bg-[#FF5A00] transition-colors z-20"
            >
                <Info size={18} />
            </button>

            {/* Tap overlay (separate from drag) */}
            <div
                className="absolute inset-0 z-10"
                onClick={cardClick}
                style={{ background: "transparent" }}
            />

            {/* Bottom gradient + info */}
            <div className="absolute bottom-0 left-0 w-full pt-20 pb-6 px-6 bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none">
                <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight mb-1 text-white">
                    {product.title}
                </h3>
                {product.price && (
                    <div className="text-lg kw-orange font-bold">
                        {product.price}
                    </div>
                )}
            </div>
        </motion.div>
    );
}
